/** @jsxImportSource preact */
import * as copy from "../paraglide/messages.js";
import { CARD_STYLES } from "./form/card-styles";
import { FORM_STYLES, formVars } from "./form/form-styles";
import type { ResolvedCompareCard, WidgetCompareColumn } from "./message";
import { SR_ONLY } from "./sr-only";
import { SERIF_META_FONT, type WidgetTokens } from "./theme";

interface CompareCardProps {
  card: ResolvedCompareCard;
  tokens: WidgetTokens;
  linksInNewTab?: boolean;
  /** 入场动画：跟着刚流完的回复出来时才有，时刻由消息排。 */
  animation?: string;
}

/**
 * 对比卡的专属规则，叠在 CARD_STYLES 之上。
 * 还是一张真表格（读屏能按行列报出「哪件商品、哪个字段」），井是靠 tbody 里的格子铺同一块底色、
 * 四个角各圆一个拼出来的：border-spacing 为 0，格子之间没有缝。
 * 两边不同的行，值升成井里的白色胶囊；两边一样的行只是安静的字。
 * Editorial 不套卡片：去掉底和阴影，行与行之间是细线，不同的值只加粗、铺井色。
 */
export const COMPARE_CARD_STYLES = `
.gn-compare-card { max-width:360px; }
.gn-cmp-table { width:100%; table-layout:fixed; border-collapse:separate; border-spacing:0; font-size:13px; line-height:1.35; }
:where(.gn-cmp-table th, .gn-cmp-table td) { padding:0; text-align:start; overflow-wrap:anywhere; }
.gn-cmp-col { padding:2px 8px 14px; vertical-align:bottom; font-size:13px; font-weight:700; line-height:1.25; }
.gn-cmp-col a { color:inherit; text-decoration:underline; text-decoration-color:var(--gn-form-dash); text-underline-offset:3px; }
.gn-cmp-col a:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; border-radius:4px; }
@media (hover: hover) { .gn-cmp-col a:hover { text-decoration-color:currentColor; } }
.gn-cmp-field { padding-inline-end:8px; font-family:var(--gn-meta-font,inherit); font-size:12px; font-weight:var(--gn-form-strong); color:var(--gn-form-quiet); vertical-align:middle; }
.gn-cmp-cell { vertical-align:middle; }
.gn-cmp-value { box-sizing:border-box; display:inline-flex; align-items:center; min-height:28px; padding:0 10px; border-radius:var(--gn-form-r-cell); font-weight:600; color:var(--gn-form-quiet); }
.gn-cmp-cell[data-differs] .gn-cmp-value { background:var(--gn-form-chip); font-weight:700; color:var(--gn-form-ink); }
.gn-compare-card:not([data-editorial]) tbody :is(th, td) { padding-block:4px; background:var(--gn-form-sheet); }
.gn-compare-card:not([data-editorial]) tbody tr > :first-child { padding-inline-start:12px; }
.gn-compare-card:not([data-editorial]) tbody tr > :last-child { padding-inline-end:12px; }
.gn-compare-card:not([data-editorial]) tbody tr:first-child > :is(th, td) { padding-top:12px; }
.gn-compare-card:not([data-editorial]) tbody tr:last-child > :is(th, td) { padding-bottom:12px; }
.gn-compare-card:not([data-editorial]) tbody tr:first-child > :first-child { border-start-start-radius:var(--gn-form-r-well); }
.gn-compare-card:not([data-editorial]) tbody tr:first-child > :last-child { border-start-end-radius:var(--gn-form-r-well); }
.gn-compare-card:not([data-editorial]) tbody tr:last-child > :first-child { border-end-start-radius:var(--gn-form-r-well); }
.gn-compare-card:not([data-editorial]) tbody tr:last-child > :last-child { border-end-end-radius:var(--gn-form-r-well); }
.gn-compare-card[data-editorial] { gap:0; padding:0; border-top:1px solid var(--gn-form-line); border-bottom:1px solid var(--gn-form-line); border-radius:0; background:transparent; box-shadow:none; }
.gn-compare-card[data-editorial] .gn-cmp-col { padding:7px 8px; }
.gn-compare-card[data-editorial] .gn-cmp-field, .gn-compare-card[data-editorial] .gn-cmp-cell { padding:7px 8px; }
.gn-compare-card[data-editorial] tbody :is(th, td) { border-top:1px solid var(--gn-form-line); }
.gn-compare-card[data-editorial] .gn-cmp-value { display:inline; min-height:0; padding:0; border-radius:0; font-weight:400; color:var(--gn-form-ink); }
.gn-compare-card[data-editorial] .gn-cmp-cell[data-differs] { background:var(--gn-form-subtle); }
.gn-compare-card[data-editorial] .gn-cmp-cell[data-differs] .gn-cmp-value { background:transparent; box-shadow:none; font-weight:700; }
`;

/** 列标题：有商品页就是链接，跟商品卡一样点了走普通跳转。 */
function ColumnTitle({
  column,
  linksInNewTab,
}: {
  column: WidgetCompareColumn;
  linksInNewTab: boolean;
}) {
  if (column.url === undefined) return <>{column.title}</>;
  return (
    <a
      href={column.url}
      rel="noreferrer"
      target={linksInNewTab ? "_blank" : undefined}
    >
      {column.title}
    </a>
  );
}

/**
 * 对比卡（PRD 2.4）：上面是两列商品名，下面井里逐行摆同一字段在两边的值；两边不同的行
 * 值升成白色胶囊，读屏在字段名后多念一句「不同」，不只靠视觉区分。
 * 数据里只有两边的原文，判不出哪边「更好」，所以没有墨黑的赢家格，也不给「选这件」按钮。
 *
 * 用真表格：读屏能按行列报出「哪件商品、哪个字段」。
 */
export function CompareCard({
  card,
  tokens: t,
  linksInNewTab = false,
  animation,
}: CompareCardProps) {
  const [first, second] = card.columns;
  return (
    <div
      class="gn-card gn-compare-card"
      data-editorial={t.editorial || undefined}
      style={{
        ...formVars(t),
        "--gn-meta-font": t.serif ? SERIF_META_FONT : "inherit",
        animation,
      }}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${COMPARE_CARD_STYLES}`}</style>
      <table class="gn-cmp-table">
        <caption style={SR_ONLY}>
          {copy.widget_compare_caption({
            first: first.title,
            second: second.title,
          })}
        </caption>
        <colgroup>
          <col style={{ width: "30%" }} />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">
              <span style={SR_ONLY}>{copy.widget_compare_field()}</span>
            </th>
            {card.columns.map((column, index) => (
              <th
                class="gn-cmp-col"
                key={`${String(index)}:${column.title}`}
                scope="col"
              >
                <ColumnTitle column={column} linksInNewTab={linksInNewTab} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {card.rows.map((row, index) => (
            <tr
              key={`${String(index)}:${row.label}`}
              data-differs={row.differs || undefined}
            >
              <th class="gn-cmp-field" scope="row">
                {row.label}
                {row.differs && (
                  <span style={SR_ONLY}> {copy.widget_compare_differs()}</span>
                )}
              </th>
              {row.values.map((value, column) => (
                <td
                  class="gn-cmp-cell"
                  data-differs={row.differs || undefined}
                  key={column}
                >
                  <span class="gn-cmp-value">{value}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
